import DropDown from "../../../shared/DropDown.tsx";
import bridge from "../../../../turbo/bridge";
import {
  faClone,
  faMessageQuote,
  faPencil,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { useMutation } from "react-query";
import { fetchApi } from "../../../../helpers/reactQueryApi.ts";
import { ActivityReply } from "../../../../@types/activities.ts";
import { useUpdateActivityReplyInCache } from "../../../../hooks/useUpdateActivityReplyInCache.ts";

type ReplyContextMenuArgs = {
  activityId: string;
  replyId: string;
  can: ActivityReply["can"];
  quoteReply: () => void;
  setShowComposer: (show: boolean) => void;
  type?: "replies" | "answers";
};

export default function ReplyContextMenu({
  activityId,
  replyId,
  can,
  quoteReply,
  setShowComposer,
  type = "replies",
}: ReplyContextMenuArgs) {
  const { removeReply } = useUpdateActivityReplyInCache();

  const { mutate: deleteReply } = useMutation(
    () =>
      fetchApi(`/activities/${activityId}/${type}/${replyId}`, {
        method: "DELETE",
      }),
    {
      onSuccess: () => removeReply(activityId, replyId, type),
    },
  );

  return (
    <DropDown.Wrapper>
      <DropDown.Toggle buttonType="normal" className="text-muted p-0" small />
      <DropDown>
        <DropDown.Item
          icon={faClone}
          text={I18n.t(
            `activitystreams.${
              type === "replies" ? "reply" : "answer"
            }.permalink_copy_link`,
          )}
          onSelect={() => {
            navigator.clipboard
              .writeText(
                `https://${Preload.current_network.host}/activities/${activityId}/${type}/${replyId}`,
              )
              .then(() =>
                toastr.success(
                  I18n.t(
                    `activitystreams.${
                      type === "replies" ? "reply" : "answer"
                    }.directlink_copied`,
                  ),
                ),
              );
          }}
        />
        {type === "replies" && (
          <DropDown.Item
            icon={faMessageQuote}
            text={I18n.t("js.activitystreams.reply.permalink_quote_reply")}
            onSelect={quoteReply}
          />
        )}
        {can.update && (
          <DropDown.Item
            icon={faPencil}
            text={I18n.t(
              `js.activitystreams.edit_${
                type === "replies" ? "comment" : "answer"
              }`,
            )}
            onSelect={() => setShowComposer(true)}
          />
        )}
        {can.destroy && (
          <DropDown.Item
            icon={faTrash}
            text={I18n.t(
              `activitystreams.delete_${
                type === "replies" ? "comment" : "answer"
              }`,
            )}
            onSelect={() =>
              bridge.confirm(I18n.t("delete_confirmation"), deleteReply)
            }
          />
        )}
      </DropDown>
    </DropDown.Wrapper>
  );
}
